import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CameraIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * Pagina publica (sin login) para subir fotos de un diagnostico desde el celular.
 * Se llega escaneando el QR generado en Diagnosticos; cada foto se sube al toque
 * y aparece en tiempo real en la incidencia abierta en la compu.
 */
const DiagnosisPhotos: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [valid, setValid] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token) { setError('Link invalido'); setValid(false); return; }
    axios.get(`/api/diagnostics/photo-session/${token}`)
      .then(() => setValid(true))
      .catch((err) => {
        setError(err.response?.data?.error || 'Link invalido o expirado');
        setValid(false);
      });
  }, [token]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0;
    for (const file of Array.from(files)) {
      try {
        const formData = new FormData();
        formData.append('image', file);
        const res = await axios.post(`/api/diagnostics/photo-session/${token}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setUploaded((prev) => [...prev, res.data.image.path]);
        ok++;
      } catch (err: any) {
        toast.error(err.response?.data?.error || 'Error al subir una foto');
        if (err.response?.status === 404) { setValid(false); setError('La sesion expiro'); break; }
      }
    }
    if (ok > 0) toast.success(`${ok} foto${ok > 1 ? 's' : ''} enviada${ok > 1 ? 's' : ''} al sistema`);
    setUploading(false);
  };

  if (valid === null) {
    return <div className="min-h-screen flex items-center justify-center text-lg">Cargando...</div>;
  }
  if (!valid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="h-16 w-16 mx-auto mb-4 text-red-500" />
          <h1 className="text-xl font-bold mb-2">Link no valido</h1>
          <p className="text-muted-foreground">{error}</p>
          <p className="text-xs text-muted-foreground mt-3">Genera un QR nuevo desde Diagnosticos en la compu.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground p-4 flex flex-col">
      <div className="max-w-md mx-auto w-full space-y-4">
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <div className="text-xs text-red-600 font-mono uppercase tracking-widest">ROCAM · Diagnostico</div>
          <div className="text-2xl font-bold mt-1">Fotos de la incidencia</div>
          <div className="text-sm text-muted-foreground">Cada foto que saques aparece al instante en la compu.</div>
          {uploaded.length > 0 && (
            <div className="flex items-center gap-1 mt-2 text-emerald-600 text-xs font-medium">
              <CheckCircleIcon className="h-4 w-4" />
              {uploaded.length} foto{uploaded.length > 1 ? 's' : ''} enviada{uploaded.length > 1 ? 's' : ''}
            </div>
          )}
        </div>

        <button
          type="button"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white rounded-xl p-6 flex flex-col items-center gap-2 shadow-lg active:scale-95 transition-transform"
        >
          <CameraIcon className="h-12 w-12" />
          <span className="text-lg font-bold">{uploading ? 'Enviando...' : 'Sacar / elegir fotos'}</span>
          <span className="text-xs opacity-80">Camara o galeria · se envian solas</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />

        {uploaded.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {uploaded.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`Foto ${i + 1}`}
                className="w-full aspect-square object-cover rounded-lg border border-border"
              />
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground text-center">
          Cuando termines, cerra esta pagina. El link vence a los 30 minutos.
        </p>
      </div>
    </div>
  );
};

export default DiagnosisPhotos;
